import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowUpRight, HardHat, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

import { EmptyState } from '../../../../components/design-system/admin/EmptyState';
import { RelativeTime } from '../../../../components/design-system/admin/RelativeTime';
import { SectionCard } from '../../../../components/design-system/admin/SectionCard';
import { TradeLeadsView, useTradeApi } from '../../../../common/hooks/use-trade-api.hook';
import { activityLabel, leadReasonLabel, leadsCheckStale } from '../trade-format';

/**
 * The professionals already among the vendor's leads.
 *
 * Checked on opening when the last check is a day old, and on demand. Each
 * row says why it was called trade; the vendor can disagree, and their call
 * sticks.
 */
export const TradeLeadsSection = () => {
    const { t } = useTranslation();
    const api = useTradeApi();
    const [view, setView] = useState<TradeLeadsView | null>(null);
    const [checking, setChecking] = useState(false);
    const autoChecked = useRef(false);

    const load = useCallback(async () => {
        const next = await api.getLeads();
        setView(next);
        return next;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const check = useCallback(async (quiet = false) => {
        setChecking(true);
        try {
            const res = await api.checkLeads();
            await load();
            if (!quiet) toast.success(t('TRADE.LEADS.CHECKED', '{{checked}} leads checked: {{trade}} look like professionals.', { checked: res.checked, trade: res.trade }));
        } catch {
            if (!quiet) toast.error(t('TRADE.ERRORS.LEADS', 'Your leads could not be checked. Try again.'));
        } finally {
            setChecking(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [load]);

    useEffect(() => {
        load()
            .then((v) => {
                if (!autoChecked.current && leadsCheckStale(v.lastCheckAt)) {
                    autoChecked.current = true;
                    void check(true);
                }
            })
            .catch(() => setView({ items: [], counts: { trade: 0, consumer: 0, unclassified: 0 }, lastCheckAt: null }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const notTrade = async (leadId: string) => {
        try {
            await api.setLeadAudience(leadId, 'consumer');
            await load();
            toast.success(t('TRADE.LEADS.MARKED_CONSUMER', 'Marked as a private customer. It stays that way.'));
        } catch {
            toast.error(t('TRADE.ERRORS.SAVE', 'The change was not saved. Try again.'));
        }
    };

    const total = view ? view.counts.trade + view.counts.consumer + view.counts.unclassified : 0;

    return (
        <SectionCard
            testId="trade-leads-section"
            title={t('TRADE.LEADS.TITLE', 'Trade leads among your leads')}
            description={view
                ? t('TRADE.LEADS.SUBTITLE', '{{trade}} of your {{total}} leads look like professionals — worth a different conversation.', { trade: view.counts.trade, total })
                : t('TRADE.LEADS.LOADING', 'Checking your leads…')}
            action={
                <Button variant="outline" size="sm" onClick={() => check(false)} disabled={checking}>
                    {checking ? <Spinner data-icon="inline-start" /> : <RefreshCw data-icon="inline-start" />}
                    {t('TRADE.LEADS.CHECK', 'Check again')}
                </Button>
            }
            flush
        >
            {!view ? (
                <Skeleton className="mx-6 mb-6 h-32" />
            ) : !view.items.length ? (
                <EmptyState
                    icon={<HardHat />}
                    title={t('TRADE.LEADS.EMPTY_TITLE', 'No professionals among your leads yet')}
                    description={t('TRADE.LEADS.EMPTY_BODY', 'We look at their email address, what they wrote to you and how much they visualised. When one looks like a fitter, an architect or a designer, they appear here.')}
                />
            ) : (
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="ps-6">{t('TRADE.LEADS.LEAD', 'Lead')}</TableHead>
                            <TableHead>{t('TRADE.LEADS.WHY', 'Why')}</TableHead>
                            <TableHead>{t('TRADE.LEADS.LAST_SEEN', 'Last seen')}</TableHead>
                            <TableHead className="pe-6"><span className="sr-only">{t('TRADE.LEADS.ACTIONS', 'Actions')}</span></TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {view.items.map((lead) => {
                            const company = lead.audienceInfo?.company;
                            return (
                                <TableRow key={lead._id} data-testid="trade-lead-row">
                                    <TableCell className="ps-6">
                                        <span className="flex min-w-0 flex-col">
                                            <span className="truncate font-medium">{lead.name || lead.email}</span>
                                            {lead.name && <span className="truncate text-xs text-muted-foreground">{lead.email}</span>}
                                            {company && (
                                                <span className="truncate text-xs text-muted-foreground">
                                                    {company.name}{company.activityCode ? ` · ${activityLabel(company.activityCode, t, company.activityCode)}` : ''}
                                                </span>
                                            )}
                                        </span>
                                    </TableCell>
                                    <TableCell>
                                        <span className="flex flex-wrap gap-1">
                                            {(lead.audienceInfo?.reasons ?? []).slice(0, 4).map((r) => (
                                                <span key={r} className="rounded-md border px-1.5 py-0.5 text-xs whitespace-nowrap">{leadReasonLabel(r, t)}</span>
                                            ))}
                                        </span>
                                    </TableCell>
                                    <TableCell className="text-sm whitespace-nowrap text-muted-foreground">
                                        <RelativeTime value={lead.lastSeenAt} />
                                    </TableCell>
                                    <TableCell className="pe-6">
                                        <span className="flex items-center justify-end gap-1">
                                            <Button variant="ghost" size="sm" onClick={() => notTrade(lead._id)}>
                                                {t('TRADE.LEADS.NOT_TRADE', 'Not a pro')}
                                            </Button>
                                            <Button asChild variant="outline" size="sm">
                                                <Link to={`/sales/leads/${lead._id}`}>
                                                    {t('TRADE.LEADS.OPEN', 'Open lead')}
                                                    <ArrowUpRight data-icon="inline-end" className="rtl:-scale-x-100" />
                                                </Link>
                                            </Button>
                                        </span>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            )}
        </SectionCard>
    );
};

export default TradeLeadsSection;
